import { IonButton, IonLoading, useIonAlert } from '@ionic/react'
import { SegmentedMessage } from 'sms-segments-calculator'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import {
  BYTES_IN_KB,
  forbiddenWords,
  MAX_ATTACHMENT_KB,
  MMS_CHARACTER_LIMIT,
  MMS_CREDIT_COUNT,
} from '../../../constants/communication'
import { useAccount, useUser } from '../../../contexts/authContext'
import { useToast } from '../../../contexts/toastContext'
import { useIsAdminRoute } from '../../../hooks/routing'
import { SchedulePicker } from '../../../components/ui/form/schedule-picker'
import { useHasRole, useIsStore } from '../../../auth/utils'
import { useBroadcastClientCounts, useBroadcastSettings } from '../queries'
import { TestBroadcastProvider } from '../context'
import {
  useDeleteBroadcast,
  useSendBroadcast,
  useTestBroadcast,
  useUpdateBroadcast,
} from '../mutations'
import {
  checkForBlockedWords,
  composeTitle,
  getMaxCharacterCount,
} from '../utils'
import { TestBroadcastButton } from './test-broadcast-button'

import type { UpdateBroadcastInputs } from '../broadcast.schema'
import type { ServerStatus } from '../../../schema/server-status.schema'

type Props = {
  admin?: boolean
  broadcastId: string
  disabled?: boolean
  onDelete: () => void
  onSubmit: () => void
  status: ServerStatus | null
}

function BroadcastButtons({
  admin,
  broadcastId,
  disabled,
  onDelete,
  onSubmit,
  status,
}: Props) {
  const setToast = useToast()
  const account = useAccount()
  const user = useUser()
  const isAdminRoute = useIsAdminRoute()
  const [presentAlert] = useIonAlert()

  const { control, getValues, handleSubmit, reset, setValue } =
    useFormContext<UpdateBroadcastInputs>()
  const watchDate = useWatch({ control, name: 'date' })

  const emailMessage = getValues('emailMessage')
  const emailSubject = getValues('emailSubject')
  const messageType = getValues('messageType')
  const smsMessage = getValues('smsMessage')
  const clientCounts = useBroadcastClientCounts(broadcastId)
  const broadcastSettings = useBroadcastSettings()

  const deleteBroadcast = useDeleteBroadcast(
    getValues('title') ?? 'This Broadcast'
  )
  const sendBroadcast = useSendBroadcast()
  const testBroadcast = useTestBroadcast()
  const updateBroadcast = useUpdateBroadcast()

  const isAdmin = useHasRole('admin')
  const { isFaherty } = useIsStore()
  const requiresApproval = !isAdmin && isFaherty()
  const canSmsMessage = isAdminRoute || user.hasTwilio
  const userHasCustomEmail =
    user.hasSendGrid && account.sendgridDomains.length > 0

  const isBroadcastDisabled =
    // Should clientcount query hold up filling out form?
    clientCounts.data?.totalCount === 0 ||
    updateBroadcast.isLoading ||
    sendBroadcast.isLoading ||
    sendBroadcast.isSuccess

  function composeDraft(currentState: UpdateBroadcastInputs) {
    const {
      collectionIdsEmail,
      collectionIdsSms,
      date,
      emailMessage,
      emailSubject,
      emailTemplateId,
      fromEmailDomain,
      fromEmailName,
      fromName,
      fromUserId,
      fromUserRule,
      lookIdsEmail,
      lookIdsSms,
      productIdsEmail,
      productIdsSms,
      setAttachmentsEmail,
      setAttachmentsSms,
      smsTemplateId,
      title,

      imglyString,
      messageType,

      // requiresApproval,
      smsMessage,
    } = currentState
    return {
      objectId: broadcastId,
      broadcast: {
        title: composeTitle(title),
        date,

        smsTemplateId,
        emailTemplateId,

        fromEmailName,
        fromEmailDomain,
        fromName,
        fromUserId,
        fromUserRule,

        messageType,

        smsMessage,
        setAttachmentsSms,
        collectionIdsSms: collectionIdsSms ?? null,
        lookIdsSms: lookIdsSms ?? null,
        productIdsSms: productIdsSms ?? null,
        imglyString,

        emailSubject,
        emailMessage,
        setAttachmentsEmail,
        collectionIdsEmail: collectionIdsEmail ?? null,
        lookIdsEmail: lookIdsEmail ?? null,
        productIdsEmail: productIdsEmail ?? null,

        requiresApproval,
      },
    }
  }

  async function validateBroadcast(data: UpdateBroadcastInputs) {
    if (!broadcastSettings.data) {
      setToast({ message: 'There was an issue loading your credits.' })
      return false
    }
    if (!clientCounts.data) {
      setToast({ message: 'There was an issue retrieving the client counts.' })
      return false
    }
    const segment = new SegmentedMessage(data.smsMessage ?? '')

    const { smsClientCount, totalCount } = clientCounts.data
    const creditsUsed = smsClientCount
      ? data.setAttachmentsSms
        ? smsClientCount * MMS_CREDIT_COUNT
        : smsClientCount * segment.segmentsCount
      : 0

    if (creditsUsed > broadcastSettings.data.smsCreditsRemaining) {
      setToast({
        message: `You do not have enough credits to ${
          data.date ? 'schedule' : 'send'
        } this broadcast. Please contact your OneShop representative to purchase more.`,
      })
      return false
    }

    if (totalCount === 0) {
      setToast({ message: 'No clients found' })
      return false
    }

    // Only need to validate the custom email if user has that feature
    // all other users will send from twilioProxyEmail by default
    if (
      userHasCustomEmail &&
      ((data.fromUserRule === 'fromUser' && !data.fromEmailName) ||
        !data.fromEmailDomain)
    ) {
      setToast({ message: 'The Sender Email Address is not filled out.' })
      return false
    }

    if (
      (data.messageType === 'sms' || data.messageType === 'prioSmsEmail') &&
      !canSmsMessage &&
      data.smsMessage?.length
    ) {
      setToast({
        message:
          'You do not have the correct permissions to send a text message. Please select Email message type.',
      })
      return false
    }

    if (
      (data.messageType === 'sms' || data.messageType === 'prioSmsEmail') &&
      canSmsMessage &&
      !data.smsMessage
    ) {
      setToast({ message: 'Missing text message' })
      return false
    }

    if (
      data.setAttachmentsSms &&
      data.smsMessage &&
      data.smsMessage.length > MMS_CHARACTER_LIMIT
    ) {
      setToast({
        message: `Your MMS text message exceeds the maximum allowed character limit of ${MMS_CHARACTER_LIMIT} characters.`,
      })
      return false
    }

    if (
      data.setAttachmentsSms &&
      data.imglyString &&
      typeof data.setAttachmentsSms[0] === 'string'
    ) {
      const response = await fetch(data.setAttachmentsSms[0], {
        method: 'head',
      })

      const contentLength = response.headers.get('Content-Length')
      if (contentLength) {
        const imageBytes = parseInt(contentLength)
        const imageKiloBytes = imageBytes / BYTES_IN_KB

        if (imageKiloBytes > MAX_ATTACHMENT_KB)
          setToast({
            message:
              'Your text attachment is too large. Please attach an image lower than 500KB',
            color: 'danger',
          })
        return false
      }
    }
    // TODO: MIGHT NOT NEED TO CHECK BOTH
    const emailMessage = data.emailMessage
    // const emailMessage = data.emailMessage || htmlEmail?.json
    if (
      (data.messageType === 'email' || data.messageType === 'prioSmsEmail') &&
      (!data.emailSubject || !emailMessage)
    ) {
      setToast({ message: 'Missing email message or subject' })
      return false
    }

    const maxCharacterCount = getMaxCharacterCount(
      broadcastSettings.data.smsSegmentLimit,
      { isNonGsm: Boolean(segment.getNonGsmCharacters().length) }
    )

    if (
      smsClientCount > 0 &&
      data.smsMessage &&
      data.smsMessage.length > maxCharacterCount
    ) {
      setToast({
        message: 'Text message is longer than the max allowed',
      })
      return false
    }

    const blockedWords = checkForBlockedWords({
      strToCheck: data.smsMessage?.toLowerCase(),
      blockedList: forbiddenWords,
    })
    if (blockedWords.length > 0) {
      setToast({
        message: `The following words are restricted: ${blockedWords.join(
          ', '
        )}. Please revise and try again.`,
      })
      return false
    }

    if (
      userHasCustomEmail &&
      data.fromUserId &&
      data.messageType !== 'sms' &&
      // should this just be a more generic if !data...
      (data.fromName == null ||
        data.fromEmailName == null ||
        data.fromEmailDomain == null)
    ) {
      setToast({
        message: 'You must set both a Sender Name and Sender Email Address.',
      })
      return false
    }

    return true
  }

  function handleDeleteBroadcast() {
    deleteBroadcast.mutate(broadcastId, {
      onSuccess: () => onDelete(),
    })
  }

  function handleSaveDraft() {
    const currentState = getValues()

    updateBroadcast.mutate(composeDraft(currentState), {
      onSuccess: () => {
        reset(currentState)
        setToast({
          message: 'Your draft was successfully saved.',
          color: 'yellow',
        })
      },
    })
  }

  async function handleSubmitForm(formData: UpdateBroadcastInputs) {
    const isValid = await validateBroadcast(formData)
    if (!isValid) return

    presentAlert({
      header: 'Alert',
      message: `You are about to
          ${formData.date ? 'schedule' : 'send'} this broadcast to ${
        clientCounts.data?.totalCount
      } customers. ${
        !formData.date ? 'This cannot be undone. Are you sure?' : ''
      }`,
      buttons: [
        'Cancel',
        {
          text: formData.date ? 'Schedule' : 'Send',
          handler: async () => {
            const { broadcast } = composeDraft(formData)
            updateBroadcast.mutate(
              {
                objectId: broadcastId,
                broadcast,
              },
              {
                onSuccess: () => {
                  sendBroadcast.mutate(broadcastId)
                  onSubmit()
                },
              }
            )
          },
        },
      ],
    })
  }

  return (
    <div className="mx-auto max-w-lg space-y-3">
      <IonButton
        color="yellow"
        fill="solid"
        expand="block"
        size="small"
        disabled={isBroadcastDisabled}
        onClick={handleSubmit(handleSubmitForm)}
      >
        {watchDate ? 'Schedule Broadcast' : 'Send Broadcast'}
      </IonButton>
      <Controller
        name="date"
        control={control}
        render={({ field: { value, onChange } }) => (
          <SchedulePicker
            label="Schedule Broadcast"
            value={value ?? null} // TODO COMPARE TO OG
            disabled={disabled}
            onChange={onChange}
            onReset={() => setValue('date', '')}
          />
        )}
      />
      <div className="flex gap-x-3">
        <IonButton
          size="small"
          color="danger"
          fill="outline"
          className="flex-grow"
          disabled={deleteBroadcast.isLoading}
          onClick={handleDeleteBroadcast}
        >
          Delete {status === 'draft' ? 'Draft' : 'Broadcast'}
        </IonButton>
        {status === 'pending' ? (
          <IonButton
            size="small"
            color="yellow"
            fill="outline"
            className="flex-grow"
            onClick={() =>
              updateBroadcast.mutate({
                objectId: broadcastId,
                broadcast: { status: 'draft', requiresApproval },
              })
            }
          >
            Revert to Draft
          </IonButton>
        ) : status === 'draft' ? (
          <>
            <IonButton
              size="small"
              color="yellow"
              fill="outline"
              className="flex-grow"
              onClick={handleSaveDraft}
            >
              Save Draft
            </IonButton>
            <TestBroadcastProvider
              value={{
                emailMessage: emailMessage ?? '',
                emailSubject: emailSubject ?? '',
                messageType: messageType ?? 'prioSmsEmail',
                smsMessage: smsMessage ?? '',
              }}
            >
              <TestBroadcastButton
                disabled={updateBroadcast.isLoading || testBroadcast.isLoading}
                onSend={(data) =>
                  updateBroadcast.mutate(composeDraft(getValues()), {
                    onSuccess: () => {
                      testBroadcast.mutate({
                        broadcastId,
                        ...data,
                      })
                    },
                  })
                }
              />
            </TestBroadcastProvider>
          </>
        ) : null}
      </div>
      <IonLoading
        isOpen={updateBroadcast.isLoading}
        message="Updating broadcast..."
      />
    </div>
  )
}

export { BroadcastButtons }
